import React, { useRef, useState } from 'react';
import capcom from '../assets/capcom.png';
import marvel from '../assets/marvel.webp';
import dc from '../assets/DC_COMICS.webp';
import sw from '../assets/STARWARS.webp';
import gb from '../assets/ghostbusters-logo.png';
import st from '../assets/stranger-things-logo.png';
import ni from '../assets/Mario_Series_Logo.png';
import xbox from '../assets/xboxonelogo.png';
import ps from '../assets/playstation.png';
import numskull from '../assets/numskull.png';
import { Col, Container, Row } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { useGetProductsQuery } from '../slices/productsApiSlice';

const IconModel = ({ src, width, height, alt, brand, queryField = 'brand', isHovered, onMouseEnter, onMouseLeave }) => {
    // Fetch products for this brand - use dynamic query field
    const queryParams = queryField === 'brand'
        ? { keyword: brand }  // Use keyword search for brand
        : { character: brand };  // Use character filter for others

    const { data, isLoading } = useGetProductsQuery({
        ...queryParams,
        pageNumber: 1
    });

    // Get first 3 products with images
    const previewProducts = data?.products
        ?.filter(p => p.image)
        ?.slice(0, 3) || [];

    return (
        <div
            style={{
                position: 'relative',
                display: 'inline-block',
                transformOrigin: 'center top',
                zIndex: isHovered ? 100 : 1
            }}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
        >
            <div style={{
                width: '120px',
                height: '70px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
            }}>
                <img
                    src={src}
                    alt={alt}
                    draggable="false"
                    style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                        opacity: isHovered ? 1 : 0.5,
                        transition: 'all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
                        transform: isHovered ? 'scale(1.15) translateY(5px)' : 'scale(1)',
                        filter: isHovered ? 'drop-shadow(0 8px 16px rgba(0,0,0,0.3))' : 'none'
                    }}
                />
            </div>

            {/* Product Preview Overlay */}
            {isHovered && previewProducts.length > 0 && (
                <div
                    style={{
                        position: 'absolute',
                        top: '100%',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        display: 'flex',
                        gap: '8px',
                        marginTop: '10px',
                        zIndex: 1000,
                        animation: 'fadeInUp 0.3s ease',
                        pointerEvents: 'none'
                    }}
                >
                    {previewProducts.map((product, idx) => (
                        <div
                            key={product._id}
                            style={{
                                width: '80px',
                                height: '80px',
                                borderRadius: '8px',
                                overflow: 'hidden',
                                boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                                border: '2px solid white',
                                animation: `popIn 0.3s ease ${idx * 0.1}s both`
                            }}
                        >
                            <img
                                src={product.image}
                                alt={product.name}
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover'
                                }}
                            />
                        </div>
                    ))}
                </div>
            )}

            <style>{`
                @keyframes fadeInUp {
                    from {
                        opacity: 0;
                        transform: translateX(-50%) translateY(10px);
                    }
                    to {
                        opacity: 1;
                        transform: translateX(-50%) translateY(0);
                    }
                }

                @keyframes popIn {
                    from {
                        opacity: 0;
                        transform: scale(0.8);
                    }
                    to {
                        opacity: 1;
                        transform: scale(1);
                    }
                }
            `}</style>
        </div>
    );
};

const ImageLayout = ({ onIconClick }) => {
    const navigate = useNavigate();
    const [isDragging, setIsDragging] = useState(false);
    const [startX, setStartX] = useState(0);
    const [scrollLeft, setScrollLeft] = useState(0);
    const [hoveredIndex, setHoveredIndex] = useState(null);

    const scrollRef = useRef(null);

    const handleIconClick = (keyword, description) => {
        if (isDragging) return;
        navigate(`/search/${keyword}`);
        if (onIconClick) {
            onIconClick(description);
        }
    };

    const startDragging = (e) => {
        setIsDragging(true);
        const pageX = e.pageX || e.touches[0].pageX;
        setStartX(pageX - scrollRef.current.offsetLeft);
        setScrollLeft(scrollRef.current.scrollLeft);
    };

    const onDragging = (e) => {
        if (!isDragging) return;
        e.preventDefault();
        const pageX = e.pageX || e.touches[0].pageX;
        const x = pageX - scrollRef.current.offsetLeft;
        const walk = (x - startX) * 0.8;
        scrollRef.current.scrollLeft = scrollLeft - walk;
    };

    const stopDragging = () => {
        setIsDragging(false);
    };

    const iconsData = [
        {
            src: marvel,
            keyword: 'Marvel',
            alt: 'Marvel logo - Browse Marvel products including Spiderman, Ironman, and Avengers merchandise',
            description: 'In a world of Spiderman, Ironman, and the Avengers, we have some great Marvel gift ideas!!'
        },
        {
            src: ps,
            keyword: 'Playstation',
            alt: 'PlayStation logo - Browse PlayStation gaming merchandise and collectibles',
            description: 'Dip into the world of gaming with our range of Playstation products!'
        },
        {
            src: ni,
            keyword: 'Mario',
            alt: 'Nintendo logo - Browse Nintendo products including Mario, Zelda, and Switch merchandise',
            description: 'Jump into the world of Mario, Zelda, and more with our range of Nintendo products!'
        },
        {
            src: dc,
            keyword: 'DC',
            alt: 'DC Comics logo - Browse DC superhero products including Superman, Batman, and Wonder Woman',
            description: 'Superman, Batman and Wonderwoman are just a few of the DC characters we have products for!'
        },
        {
            src: st,
            keyword: 'Stranger Things',
            alt: 'Stranger Things logo - Browse Stranger Things merchandise and collectibles',
            description: 'In the upside down world of Stranger Things, we have the best products for you!'
        },
        {
            src: gb,
            keyword: 'Ghostbusters',
            alt: 'Ghostbusters logo - Browse Ghostbusters merchandise and collectibles',
            description: 'If theres something strange in your neighbourhood, who you gonna call? Ghostbusters!'
        },
        {
            src: sw,
            keyword: 'Star Wars',
            alt: 'Star Wars logo - Browse Star Wars merchandise including movies and TV series collectibles',
            description: 'These are not the droids you are looking for, but we have other Star Wars products for you!'
        },
        {
            src: xbox,
            keyword: 'Xbox',
            alt: 'Xbox logo - Browse Xbox gaming merchandise including Halo and Gears of War products',
            description: 'Halo and Gears of War are just a few of the Xbox games that inspire our range of products!'
        },
        {
            src: capcom,
            keyword: 'Capcom',
            alt: 'Capcom logo - Browse Capcom gaming merchandise including Street Fighter to Resident Evil',
            description: 'Hadooken! Capcom has some of the best games and products for you!'
        },
        {
            src: numskull,
            keyword: 'Numskull',
            alt: 'Numskull logo - Browse Numskull retro ducks merchandise including Robo Cop to the Wizard of Oz',
            description: 'Quack Quack! Numskull’s got the ducks you didn’t know you needed.'
        },
    ];

    return (
        <Container fluid>
            <Row
                ref={scrollRef}
                className="horizontal-scroll"
                style={{
                    cursor: isDragging ? 'grabbing' : 'grab',
                    paddingBottom: '120px' // Space for product previews
                }}
                onMouseDown={startDragging}
                onMouseMove={onDragging}
                onMouseLeave={stopDragging}
                onMouseUp={stopDragging}
                onTouchStart={startDragging}
                onTouchMove={onDragging}
                onTouchEnd={stopDragging}
            >
                {iconsData.map((icon, index) => (
                    <Col
                        key={index}
                        onClick={() => handleIconClick(icon.keyword, icon.description)}
                        className="col"
                        style={{
                            width: 'auto',
                            padding: '0px',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            marginRight: '70px'
                        }}
                        sm={12} md={6} lg={4} xl={3}
                    >
                        <IconModel
                            src={icon.src}
                            width="100%"
                            height="auto"
                            alt={icon.alt}
                            brand={icon.keyword}
                            queryField={icon.queryField}
                            isHovered={hoveredIndex === index}
                            onMouseEnter={() => setHoveredIndex(index)}
                            onMouseLeave={() => setHoveredIndex(null)}
                        />
                    </Col>
                ))}
            </Row>
        </Container>
    );
};

export default ImageLayout;